import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User } from '../types';

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  updateBalance: (newBalance: number) => void;
  refreshUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ user: User | null; isLoading: boolean; children: ReactNode }> = ({
  user: initialUser,
  isLoading,
  children,
}) => {
  const [user, setUser] = useState<User | null>(initialUser);

  // Update local user when prop changes
  useEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  const updateBalance = (newBalance: number) => {
    if (user) {
      setUser({
        ...user,
        balance: newBalance
      } as User);
    }
  };

  const refreshUser = () => {
    setUser(initialUser);
  };

  return (
    <UserContext.Provider value={{ user, isLoading, updateBalance, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

